/**
 * Correlation id joining every step of one person's invitation → registration →
 * first-login journey (XT-706). Support searches this one token to see where a
 * user got stuck.
 *
 * Derived from the email rather than stored, so no invite carries an extra
 * column and both the API and the browser can compute the same id from the same
 * input with nothing threaded between them.
 */

/** Used when the email is not known yet — the ticket-strategy sign-up landing. */
export const TRACE_ID_UNKNOWN = 'unknown';

/**
 * MUST match the comparison `findOrCreate` uses to link a Clerk user to an
 * invited row (`lower(email) = lower(:email)`, accounts.service.ts). If these
 * ever diverge, the invite-time id and the webhook-time id stop being the same
 * string and the whole trace silently splits in two.
 */
const normalizeEmail = (email: string): string => email.trim().toLowerCase();

/**
 * FNV-1a (32-bit). Deliberately NOT a cryptographic hash:
 *
 * - The browser's only built-in digest, `crypto.subtle.digest`, is ASYNC. Making
 *   this async would force every call site to await — including a React
 *   `useState` initializer that must stay synchronous, and the fire-and-forget
 *   beacon path that never awaits anything.
 * - `crypto.subtle` is `undefined` on insecure origins, so a LAN dev host on
 *   plain http would throw in exactly the place this flow gets debugged.
 * - Node's `createHash` is server-only, so it cannot back a shared helper.
 *
 * Nothing is authorised by this id and the email travels in the adjacent field,
 * so there is no secret to protect. A collision is cosmetic: two journeys share
 * a token in Slack search, and every line still names its own address.
 */
const fnv1a = (input: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    // 32-bit FNV prime (16777619) via shifts — `*` would lose precision.
    hash +=
      (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return hash >>> 0;
};

/**
 * `a3f2b19c` — stable for a given email, case- and whitespace-insensitive.
 * A blank or missing email yields `unknown` rather than throwing, so a trace
 * step never fails the flow it is describing.
 */
export const invitationTraceId = (email: string | null | undefined): string => {
  if (!email) return TRACE_ID_UNKNOWN;
  const normalized = normalizeEmail(email);
  if (normalized.length === 0) return TRACE_ID_UNKNOWN;
  // Full 32 bits as 8 hex digits — the same on-screen width the old `inv-` +
  // 6 form occupied, with 256x the space, so ids stay aligned in Slack and
  // collisions are correspondingly rarer.
  return fnv1a(normalized).toString(16).padStart(8, '0');
};
